import React from 'react';
import { Plus } from 'lucide-react';
import {
  DOCUMENT_CATEGORY_OPTIONS,
  REMINDER_TYPE_OPTIONS,
  REPAIR_STATUS_OPTIONS,
  type DocumentCategory,
  type ReminderDueState,
  type RepairStatus,
  type VehicleDocument,
  type VehicleReminder,
  type VehicleRepair,
  type VehicleServiceEvent,
} from '../types';
import {
  daysText,
  formatBytes,
  formatCents,
  formatIsoDate,
  reminderStateLabel,
} from '../format';
import {
  DocumentCategoryBadge,
  ReminderStateBadge,
  ReminderTypeLabel,
  RepairStatusBadge,
} from './FuhrparkStatusBadge';
import {
  EmptyState,
  FUHRPARK_BTN_GREEN,
  FUHRPARK_TABLE_PAGE_SIZE,
  FuhrparkFilterChip,
  FuhrparkListPagination,
  FuhrparkSearchInput,
  FuhrparkSortableHeader,
  type FuhrparkSortDir,
} from './fuhrparkUi';

const REMINDER_STATE_FILTERS: Array<{ value: ReminderDueState; label: string }> = [
  { value: 'overdue', label: 'Überfällig' },
  { value: 'soon', label: 'Bald fällig' },
  { value: 'ok', label: 'Später fällig' },
  { value: 'done', label: 'Erledigt' },
];

function localeCmp(a: string, b: string): number {
  return a.localeCompare(b, 'de', { sensitivity: 'base' });
}

function useDebouncedQuery(value: string): string {
  const [debounced, setDebounced] = React.useState('');
  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(value.trim().toLowerCase()), 250);
    return () => window.clearTimeout(t);
  }, [value]);
  return debounced;
}

function pageSlice<T>(rows: T[], page: number, pageSize: number): { safePage: number; pagedRows: T[] } {
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  return {
    safePage,
    pagedRows: rows.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
}

function DetailTableToolbar({
  empty,
  canEdit,
  search,
  onSearch,
  searchPlaceholder,
  addLabel,
  onAdd,
  chips,
  beforeSearch,
}: {
  empty: boolean;
  canEdit: boolean;
  search: string;
  onSearch: (value: string) => void;
  searchPlaceholder: string;
  addLabel: string;
  onAdd?: () => void;
  chips: React.ReactNode;
  beforeSearch?: React.ReactNode;
}) {
  if (!canEdit && empty && !beforeSearch) return null;
  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      {empty ? null : (
        <div className="flex w-full flex-nowrap gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] md:w-auto md:flex-wrap md:overflow-visible md:pb-0 [&::-webkit-scrollbar]:hidden">
          {chips}
        </div>
      )}
      <div
        className={`grid w-full grid-cols-2 items-center gap-2 md:flex md:w-auto ${
          empty ? 'md:ml-auto' : ''
        }`}
      >
        {beforeSearch}
        {empty ? null : (
          <div className={`col-span-2 min-w-0 md:order-none md:w-64 ${beforeSearch ? 'order-last' : ''}`}>
            <FuhrparkSearchInput value={search} onChange={onSearch} placeholder={searchPlaceholder} />
          </div>
        )}
        {canEdit && onAdd ? (
          <button
            type="button"
            className={`${FUHRPARK_BTN_GREEN} w-full md:w-auto ${beforeSearch ? '' : 'col-span-2'}`}
            onClick={onAdd}
          >
            <Plus className="h-3.5 w-3.5" />
            {addLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MaintenanceViewToggle({
  view,
  onChange,
}: {
  view: 'plan' | 'history';
  onChange: (view: 'plan' | 'history') => void;
}) {
  return (
    <div
      className="inline-flex h-9 w-full shrink-0 rounded-md border border-app-border bg-app-surface-muted p-0.5 md:w-auto"
      role="tablist"
      aria-label="Wartungsansicht"
    >
      {(['plan', 'history'] as const).map((key) => {
        const active = view === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(key)}
            className={`inline-flex h-8 flex-1 items-center justify-center rounded px-3 text-sm font-semibold transition-colors md:flex-none ${
              active ? 'bg-app-surface text-app-text shadow-sm' : 'text-app-text-subtle hover:text-app-text-muted'
            }`}
          >
            {key === 'plan' ? 'Planung' : 'Historie'}
          </button>
        );
      })}
    </div>
  );
}

export function DocumentsTab({
  documents,
  canEdit,
  onUpload,
  onPreview,
  onDelete,
}: {
  documents: VehicleDocument[];
  canEdit: boolean;
  onUpload: () => void;
  onPreview: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  type SortKey = 'name' | 'type' | 'date' | 'size';
  const [filter, setFilter] = React.useState<DocumentCategory | 'all'>('all');
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedQuery(search);
  const [sort, setSort] = React.useState<{ key: SortKey; dir: FuhrparkSortDir }>({
    key: 'date',
    dir: 'desc',
  });
  const [page, setPage] = React.useState(1);

  const rows = React.useMemo(() => {
    const filtered = documents.filter((doc) => {
      if (filter !== 'all' && doc.category !== filter) return false;
      if (!debouncedSearch) return true;
      const hay = [doc.display_name, doc.filename, documentCategoryLabel(doc.category)]
        .join(' ')
        .toLowerCase();
      return hay.includes(debouncedSearch);
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.key === 'name') return localeCmp(a.display_name, b.display_name) * sign;
      if (sort.key === 'type') {
        return localeCmp(documentCategoryLabel(a.category), documentCategoryLabel(b.category)) * sign;
      }
      if (sort.key === 'date') {
        return (a.created_at.localeCompare(b.created_at) || localeCmp(a.display_name, b.display_name)) * sign;
      }
      return (a.size_bytes - b.size_bytes || localeCmp(a.display_name, b.display_name)) * sign;
    });
  }, [documents, filter, debouncedSearch, sort]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, sort]);

  const { safePage, pagedRows } = pageSlice(rows, page, FUHRPARK_TABLE_PAGE_SIZE);
  const empty = documents.length === 0;

  return (
    <div className="space-y-3">
      <DetailTableToolbar
        empty={empty}
        canEdit={canEdit}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Dokumente suchen…"
        addLabel="Dokument hochladen"
        onAdd={onUpload}
        chips={
          <>
            <FuhrparkFilterChip
              label="Alle"
              count={documents.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {DOCUMENT_CATEGORY_OPTIONS.map((opt) => (
              <FuhrparkFilterChip
                key={opt.value}
                label={opt.label}
                count={documents.filter((doc) => doc.category === opt.value).length}
                active={filter === opt.value}
                onClick={() => setFilter(opt.value)}
              />
            ))}
          </>
        }
      />
      {empty ? (
        <EmptyState text="Noch keine Dokumente hinterlegt." />
      ) : pagedRows.length === 0 ? (
        <EmptyState text="Keine Dokumente gefunden." />
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {pagedRows.map((d) => (
              <article
                key={d.id}
                className="cursor-pointer rounded-xl border border-app-border bg-app-surface p-3"
                onClick={() => onPreview(d.id)}
              >
                <p className="font-medium text-app-text">{d.display_name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-app-text-subtle">
                  <DocumentCategoryBadge category={d.category} />
                  <span>{formatIsoDate(d.created_at)}</span>
                  <span>{formatBytes(d.size_bytes)}</span>
                </div>
                {canEdit ? (
                  <button
                    type="button"
                    className="mt-2 text-sm text-rose-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(d.id);
                    }}
                  >
                    Löschen
                  </button>
                ) : null}
              </article>
            ))}
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
          <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[40%]" />
                <col className="w-[18%]" />
                <col className="w-[16%]" />
                <col className="w-[14%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-app-surface-muted text-left">
                <tr>
                  <FuhrparkSortableHeader
                    label="Name"
                    active={sort.key === 'name'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'name')}
                  />
                  <FuhrparkSortableHeader
                    label="Typ"
                    active={sort.key === 'type'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'type')}
                  />
                  <FuhrparkSortableHeader
                    label="Datum"
                    active={sort.key === 'date'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'date')}
                  />
                  <FuhrparkSortableHeader
                    label="Größe"
                    active={sort.key === 'size'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'size')}
                  />
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((d) => (
                  <tr
                    key={d.id}
                    className="cursor-pointer border-t border-app-border hover:bg-app-hover"
                    onClick={() => onPreview(d.id)}
                  >
                    <td className="min-w-0 px-4 py-3 font-medium text-app-text">{d.display_name}</td>
                    <td className="min-w-0 px-4 py-3">
                      <DocumentCategoryBadge category={d.category} />
                    </td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">{formatIsoDate(d.created_at)}</td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">{formatBytes(d.size_bytes)}</td>
                    <td className="px-4 py-3 text-right">
                      {canEdit ? (
                        <button
                          type="button"
                          className="text-sm text-rose-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(d.id);
                          }}
                        >
                          Löschen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}

export function MaintenanceTab({
  reminders,
  serviceEvents,
  canEdit,
  onAdd,
  onEdit,
  onComplete,
  onReopen,
  onDelete,
  onPreviewDoc,
}: {
  reminders: VehicleReminder[];
  serviceEvents: VehicleServiceEvent[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onComplete: (id: string) => void;
  onReopen: (id: string) => void;
  onDelete: (id: string) => void;
  onPreviewDoc: (id: string) => void;
}) {
  type SortKey = 'label' | 'type' | 'status' | 'due';
  const [filter, setFilter] = React.useState<ReminderDueState | 'all'>('all');
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedQuery(search);
  const [sort, setSort] = React.useState<{ key: SortKey; dir: FuhrparkSortDir }>({
    key: 'due',
    dir: 'asc',
  });
  const [page, setPage] = React.useState(1);
  const [view, setView] = React.useState<'plan' | 'history'>('plan');

  const rows = React.useMemo(() => {
    const filtered = reminders.filter((item) => {
      if (filter !== 'all' && item.due_state !== filter) return false;
      if (!debouncedSearch) return true;
      const hay = [
        item.display_label,
        item.label,
        reminderTypeLabel(item.reminder_type),
        item.interval_note,
        reminderStateLabel(item.due_state),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(debouncedSearch);
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.key === 'label') return localeCmp(a.display_label, b.display_label) * sign;
      if (sort.key === 'type') {
        return localeCmp(reminderTypeLabel(a.reminder_type), reminderTypeLabel(b.reminder_type)) * sign;
      }
      if (sort.key === 'status') return localeCmp(a.due_state, b.due_state) * sign;
      return ((a.due_on ?? '9999').localeCompare(b.due_on ?? '9999') || localeCmp(a.display_label, b.display_label)) * sign;
    });
  }, [reminders, filter, debouncedSearch, sort]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, sort]);

  const { safePage, pagedRows } = pageSlice(rows, page, FUHRPARK_TABLE_PAGE_SIZE);
  const empty = reminders.length === 0;

  const viewToggle = <MaintenanceViewToggle view={view} onChange={setView} />;

  return (
    <div className="space-y-3">
      {view === 'history' ? (
        <ServiceHistoryTable
          events={serviceEvents}
          canEdit={canEdit}
          onAdd={onAdd}
          onPreviewDoc={onPreviewDoc}
          viewToggle={viewToggle}
        />
      ) : (
      <>
      <DetailTableToolbar
        empty={empty}
        canEdit={canEdit}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Erinnerungen suchen…"
        addLabel="Erinnerung einrichten"
        onAdd={onAdd}
        beforeSearch={viewToggle}
        chips={
          <>
            <FuhrparkFilterChip
              label="Alle"
              count={reminders.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {REMINDER_STATE_FILTERS.map((opt) => (
              <FuhrparkFilterChip
                key={opt.value}
                label={opt.label}
                count={reminders.filter((item) => item.due_state === opt.value).length}
                active={filter === opt.value}
                onClick={() => setFilter(opt.value)}
              />
            ))}
          </>
        }
      />
      {empty ? (
        <EmptyState text="Keine offen" />
      ) : pagedRows.length === 0 ? (
        <EmptyState text="Keine Erinnerungen gefunden." />
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {pagedRows.map((r) => (
              <article
                key={r.id}
                className={`rounded-xl border border-app-border bg-app-surface p-3 ${
                  canEdit ? 'cursor-pointer' : ''
                }`}
                onClick={() => canEdit && onEdit(r.id)}
              >
                <p className="font-medium text-app-text">{r.display_label}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-app-text-subtle">
                  <ReminderTypeLabel type={r.reminder_type} />
                  <ReminderStateBadge state={r.due_state} />
                  <span>{formatIsoDate(r.due_on)}</span>
                </div>
                {canEdit ? (
                  <div className="mt-3 flex items-center gap-2 border-t border-app-border pt-3">
                    <ReminderDoneButton
                      done={r.due_state === 'done'}
                      className="flex-1"
                      onToggle={() => (r.due_state === 'done' ? onReopen(r.id) : onComplete(r.id))}
                    />
                    <button
                      type="button"
                      className="inline-flex h-9 items-center justify-center rounded-md px-3 text-sm font-medium text-rose-600 hover:bg-rose-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(r.id);
                      }}
                    >
                      Löschen
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
          <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[24%]" />
                <col className="w-[24%]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-app-surface-muted text-left">
                <tr>
                  <FuhrparkSortableHeader
                    label="Bezeichnung"
                    active={sort.key === 'label'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'label')}
                  />
                  <FuhrparkSortableHeader
                    label="Typ"
                    active={sort.key === 'type'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'type')}
                  />
                  <FuhrparkSortableHeader
                    label="Status"
                    active={sort.key === 'status'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'status')}
                  />
                  <FuhrparkSortableHeader
                    label="Nächster Termin"
                    active={sort.key === 'due'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'due')}
                  />
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t border-app-border ${
                      canEdit ? 'cursor-pointer hover:bg-app-hover' : ''
                    }`}
                    onClick={() => canEdit && onEdit(r.id)}
                  >
                    <td className="min-w-0 px-4 py-3 font-medium text-app-text">{r.display_label}</td>
                    <td className="min-w-0 px-4 py-3">
                      <ReminderTypeLabel type={r.reminder_type} />
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      <ReminderStateBadge state={r.due_state} />
                    </td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">
                      <span className="truncate">
                        {formatIsoDate(r.due_on)}
                        {daysText(r.days) ? (
                          <span className="text-app-text-subtle"> · {daysText(r.days)}</span>
                        ) : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit ? (
                        <div className="flex items-center justify-end gap-2">
                          <ReminderDoneButton
                            done={r.due_state === 'done'}
                            onToggle={() => (r.due_state === 'done' ? onReopen(r.id) : onComplete(r.id))}
                          />
                          <button
                            type="button"
                            className="text-sm text-rose-600 hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(r.id);
                            }}
                          >
                            Löschen
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

export function RepairsTab({
  repairs,
  canEdit,
  onAdd,
  onEdit,
  onDelete,
}: {
  repairs: VehicleRepair[];
  canEdit: boolean;
  onAdd: () => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}) {
  type SortKey = 'title' | 'workshop' | 'date' | 'cost' | 'status';
  const [filter, setFilter] = React.useState<RepairStatus | 'all'>('all');
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedQuery(search);
  const [sort, setSort] = React.useState<{ key: SortKey; dir: FuhrparkSortDir }>({
    key: 'date',
    dir: 'desc',
  });
  const [page, setPage] = React.useState(1);

  const rows = React.useMemo(() => {
    const filtered = repairs.filter((item) => {
      if (filter !== 'all' && item.status !== filter) return false;
      if (!debouncedSearch) return true;
      const hay = [item.title, item.workshop, repairStatusLabel(item.status)].join(' ').toLowerCase();
      return hay.includes(debouncedSearch);
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.key === 'title') return localeCmp(a.title, b.title) * sign;
      if (sort.key === 'workshop') return localeCmp(a.workshop, b.workshop) * sign;
      if (sort.key === 'status') return localeCmp(a.status, b.status) * sign;
      if (sort.key === 'cost') return ((a.cost_cents ?? -1) - (b.cost_cents ?? -1)) * sign;
      return ((a.repair_date ?? '').localeCompare(b.repair_date ?? '') || localeCmp(a.title, b.title)) * sign;
    });
  }, [repairs, filter, debouncedSearch, sort]);

  React.useEffect(() => {
    setPage(1);
  }, [filter, debouncedSearch, sort]);

  const { safePage, pagedRows } = pageSlice(rows, page, FUHRPARK_TABLE_PAGE_SIZE);
  const empty = repairs.length === 0;

  return (
    <div className="space-y-3">
      <DetailTableToolbar
        empty={empty}
        canEdit={canEdit}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Reparaturen suchen…"
        addLabel="Reparatur erfassen"
        onAdd={onAdd}
        chips={
          <>
            <FuhrparkFilterChip
              label="Alle"
              count={repairs.length}
              active={filter === 'all'}
              onClick={() => setFilter('all')}
            />
            {REPAIR_STATUS_OPTIONS.map((opt) => (
              <FuhrparkFilterChip
                key={opt.value}
                label={opt.label}
                count={repairs.filter((item) => item.status === opt.value).length}
                active={filter === opt.value}
                onClick={() => setFilter(opt.value)}
              />
            ))}
          </>
        }
      />
      {empty ? (
        <EmptyState text="Keine Reparaturen erfasst." />
      ) : pagedRows.length === 0 ? (
        <EmptyState text="Keine Reparaturen gefunden." />
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {pagedRows.map((r) => (
              <article
                key={r.id}
                className={`rounded-xl border border-app-border bg-app-surface p-3 ${
                  canEdit ? 'cursor-pointer' : ''
                }`}
                onClick={() => canEdit && onEdit(r.id)}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-app-text">{r.title}</p>
                  <RepairStatusBadge status={r.status} />
                </div>
                <p className="mt-1 text-xs text-app-text-subtle">
                  {formatIsoDate(r.repair_date)} · {r.workshop || '—'} ·{' '}
                  {r.cost_cents != null ? formatCents(r.cost_cents) : '—'}
                </p>
                {canEdit ? (
                  <button
                    type="button"
                    className="mt-2 text-sm text-rose-600 hover:underline"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(r.id);
                    }}
                  >
                    Löschen
                  </button>
                ) : null}
              </article>
            ))}
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
          <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[18%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[16%]" />
                <col className="w-[14%]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-app-surface-muted text-left">
                <tr>
                  <FuhrparkSortableHeader
                    label="Bezeichnung"
                    active={sort.key === 'title'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'title')}
                  />
                  <FuhrparkSortableHeader
                    label="Werkstatt"
                    active={sort.key === 'workshop'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'workshop')}
                  />
                  <FuhrparkSortableHeader
                    label="Datum"
                    active={sort.key === 'date'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'date')}
                  />
                  <FuhrparkSortableHeader
                    label="Betrag"
                    active={sort.key === 'cost'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'cost')}
                  />
                  <FuhrparkSortableHeader
                    label="Status"
                    active={sort.key === 'status'}
                    dir={sort.dir}
                    onSort={() => toggleSort(setSort, sort, 'status')}
                  />
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t border-app-border ${
                      canEdit ? 'cursor-pointer hover:bg-app-hover' : ''
                    }`}
                    onClick={() => canEdit && onEdit(r.id)}
                  >
                    <td className="min-w-0 px-4 py-3 font-medium text-app-text">{r.title}</td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">{r.workshop || '—'}</td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">{formatIsoDate(r.repair_date) || '—'}</td>
                    <td className="min-w-0 px-4 py-3 text-app-text-muted">
                      {r.cost_cents != null ? formatCents(r.cost_cents) : '—'}
                    </td>
                    <td className="min-w-0 px-4 py-3">
                      <RepairStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {canEdit ? (
                        <button
                          type="button"
                          className="text-sm text-rose-600 hover:underline"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(r.id);
                          }}
                        >
                          Löschen
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <FuhrparkListPagination
              total={rows.length}
              page={safePage}
              pageSize={FUHRPARK_TABLE_PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </>
      )}
    </div>
  );
}

function ServiceHistoryTable({
  events,
  canEdit,
  onAdd,
  onPreviewDoc,
  viewToggle,
}: {
  events: VehicleServiceEvent[];
  canEdit: boolean;
  onAdd: () => void;
  onPreviewDoc: (id: string) => void;
  viewToggle?: React.ReactNode;
}) {
  type SortKey = 'title' | 'date' | 'workshop' | 'cost';
  const [search, setSearch] = React.useState('');
  const debouncedSearch = useDebouncedQuery(search);
  const [sort, setSort] = React.useState<{ key: SortKey; dir: FuhrparkSortDir }>({
    key: 'date',
    dir: 'desc',
  });
  const [page, setPage] = React.useState(1);

  const rows = React.useMemo(() => {
    const filtered = events.filter((item) => {
      if (!debouncedSearch) return true;
      const hay = [item.title, item.workshop, item.note, reminderTypeLabel(item.reminder_type ?? 'other')]
        .join(' ')
        .toLowerCase();
      return hay.includes(debouncedSearch);
    });
    const sign = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      if (sort.key === 'title') return localeCmp(a.title, b.title) * sign;
      if (sort.key === 'workshop') return localeCmp(a.workshop, b.workshop) * sign;
      if (sort.key === 'cost') return ((a.cost_cents ?? -1) - (b.cost_cents ?? -1)) * sign;
      return ((a.performed_on ?? '').localeCompare(b.performed_on ?? '') || localeCmp(a.title, b.title)) * sign;
    });
  }, [events, debouncedSearch, sort]);

  React.useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sort]);

  const { safePage, pagedRows } = pageSlice(rows, page, FUHRPARK_TABLE_PAGE_SIZE);
  const addButton = canEdit ? (
    <button type="button" className={`${FUHRPARK_BTN_GREEN} w-full md:w-auto`} onClick={onAdd}>
      <Plus className="h-3.5 w-3.5" />
      Erinnerung einrichten
    </button>
  ) : null;

  if (events.length === 0) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 items-center gap-2 md:flex md:justify-end">
          {viewToggle}
          {addButton}
        </div>
        <EmptyState text="Noch keine Servicehistorie." />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 items-center gap-2 md:flex md:justify-end">
        {viewToggle}
        <div className="order-last col-span-2 min-w-0 md:order-none md:w-64">
          <FuhrparkSearchInput value={search} onChange={setSearch} placeholder="Historie suchen…" />
        </div>
        {addButton}
      </div>
      {pagedRows.length === 0 ? (
        <EmptyState text="Keine Einträge gefunden." />
      ) : (
      <>
      <div className="hidden overflow-hidden rounded-xl border border-app-border bg-app-surface md:block">
        <table className="w-full table-fixed text-sm">
          <thead className="sticky top-0 z-10 bg-app-surface-muted text-left">
            <tr>
              <FuhrparkSortableHeader label="Service" active={sort.key === 'title'} dir={sort.dir} onSort={() => toggleSort(setSort, sort, 'title')} />
              <FuhrparkSortableHeader label="Datum" active={sort.key === 'date'} dir={sort.dir} onSort={() => toggleSort(setSort, sort, 'date')} />
              <FuhrparkSortableHeader label="Werkstatt" active={sort.key === 'workshop'} dir={sort.dir} onSort={() => toggleSort(setSort, sort, 'workshop')} />
              <FuhrparkSortableHeader label="Betrag" active={sort.key === 'cost'} dir={sort.dir} onSort={() => toggleSort(setSort, sort, 'cost')} />
              <th className="px-4 py-2.5 text-xs font-medium text-app-text-muted">Beleg</th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map((event) => (
              <tr key={event.id} className="border-t border-app-border">
                <td className="min-w-0 px-4 py-3">
                  <p className="font-medium text-app-text">{event.title}</p>
                  {event.mileage != null ? (
                    <p className="text-xs text-app-text-subtle">{event.mileage.toLocaleString('de-DE')} km</p>
                  ) : null}
                </td>
                <td className="min-w-0 px-4 py-3 text-app-text-muted">{formatIsoDate(event.performed_on)}</td>
                <td className="min-w-0 px-4 py-3 text-app-text-muted">{event.workshop || '—'}</td>
                <td className="min-w-0 px-4 py-3 text-app-text-muted">
                  {event.cost_cents != null ? formatCents(event.cost_cents) : '—'}
                </td>
                <td className="min-w-0 px-4 py-3">
                  {event.documents?.[0] ? (
                    <button
                      type="button"
                      className="text-sm text-pipedrive-blue hover:underline"
                      onClick={() => onPreviewDoc(event.documents[0].id)}
                    >
                      {event.documents[0].display_name}
                    </button>
                  ) : (
                    <span className="text-app-text-subtle">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <FuhrparkListPagination total={rows.length} page={safePage} pageSize={FUHRPARK_TABLE_PAGE_SIZE} onPageChange={setPage} />
      </div>
      <div className="space-y-2 md:hidden">
        {pagedRows.map((event) => (
          <article key={event.id} className="rounded-xl border border-app-border bg-app-surface p-3">
            <p className="font-medium text-app-text">{event.title}</p>
            <p className="mt-1 text-xs text-app-text-subtle">
              {formatIsoDate(event.performed_on)} · {event.workshop || '—'} ·{' '}
              {event.cost_cents != null ? formatCents(event.cost_cents) : '—'}
            </p>
          </article>
        ))}
        <FuhrparkListPagination total={rows.length} page={safePage} pageSize={FUHRPARK_TABLE_PAGE_SIZE} onPageChange={setPage} />
      </div>
      </>
      )}
    </div>
  );
}

function ReminderDoneButton({
  done,
  onToggle,
  className = '',
}: {
  done: boolean;
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={`inline-flex h-9 items-center justify-center rounded-md px-3.5 text-sm font-semibold transition-colors ${
        done
          ? 'border border-app-border bg-app-surface text-app-text-muted hover:bg-app-hover'
          : 'bg-pipedrive-green text-white hover:bg-[#218838]'
      } ${className}`}
    >
      {done ? 'Wieder öffnen' : 'Erledigt'}
    </button>
  );
}

function documentCategoryLabel(category: string): string {
  return DOCUMENT_CATEGORY_OPTIONS.find((o) => o.value === category)?.label ?? category;
}

function reminderTypeLabel(type: string): string {
  return REMINDER_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

function repairStatusLabel(status: RepairStatus): string {
  return REPAIR_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

function toggleSort<K extends string>(
  setSort: React.Dispatch<React.SetStateAction<{ key: K; dir: FuhrparkSortDir }>>,
  current: { key: K; dir: FuhrparkSortDir },
  key: K,
) {
  setSort(current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
}
